import { useMemo } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import type { Source } from "@/types/api";
import { CitationChip } from "@/components/CitationChip";
import { CodeBlock } from "@/components/CodeBlock";
import { CitationsProvider, rehypeCitationChips } from "@/lib/citations";

const sanitizeSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    a: [
      ...(defaultSchema.attributes?.a ?? []),
      ["target"],
      ["rel"],
    ],
    span: [
      ...(defaultSchema.attributes?.span ?? []),
      ["className"],
      ["data-citation-index"],
    ],
    code: [
      ...(defaultSchema.attributes?.code ?? []),
      ["className"],
    ],
  },
  clobberPrefix: "ndc-md-",
};

function readCodeText(children: unknown): string {
  if (typeof children === "string") return children;
  if (Array.isArray(children)) return children.map(readCodeText).join("");
  if (children && typeof children === "object" && "props" in children) {
    const props = (children as { props?: { children?: unknown } }).props;
    return readCodeText(props?.children);
  }
  return "";
}

function languageFromClassName(className: unknown): string | undefined {
  if (typeof className !== "string") return undefined;
  const match = /language-([a-z0-9+#]+)/i.exec(className);
  return match?.[1];
}

interface MarkdownProps {
  text: string;
  sources?: Source[];
  sourceListId?: string;
}

export function Markdown({ text, sources = [], sourceListId = "ndc-sources" }: MarkdownProps) {
  const normalized = useMemo(() => String(text ?? ""), [text]);

  const rehypePlugins = useMemo(
    () => [
      [rehypeCitationChips, { maxIndex: sources.length }] as const,
      [rehypeSanitize, sanitizeSchema] as const,
    ],
    [sources.length],
  );

  const components: Components = useMemo(
    () => ({
      a: ({ node: _node, ...props }) => (
        <a {...props} target="_blank" rel="noopener noreferrer" />
      ),
      span: ({ node, className, children, ...rest }) => {
        const indexAttr = (node?.properties as Record<string, unknown> | undefined)?.[
          "data-citation-index"
        ];
        if (typeof indexAttr === "string" || typeof indexAttr === "number") {
          const idx = parseInt(String(indexAttr), 10);
          if (Number.isFinite(idx)) {
            return <CitationChip index={idx} />;
          }
        }
        return (
          <span className={className} {...rest}>
            {children}
          </span>
        );
      },
      code: ({ node: _node, className, children, ...rest }) => {
        const language = languageFromClassName(className);
        const isBlock = Boolean(language);
        if (!isBlock) {
          return (
            <code className={className} {...rest}>
              {children}
            </code>
          );
        }
        const codeText = readCodeText(children).replace(/\n$/, "");
        return <CodeBlock code={codeText} language={language} />;
      },
      pre: ({ children }) => <>{children}</>,
    }),
    [],
  );

  return (
    <CitationsProvider sourceListId={sourceListId} sources={sources}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={rehypePlugins as never}
        components={components}
      >
        {normalized}
      </ReactMarkdown>
    </CitationsProvider>
  );
}
